import { ClockCircleOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { matchesTokens, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import type { AlignType } from '@rc-component/table/lib/interface';
import { App, Button, Flex, Input, Select, Table, Tag, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ColumnType } from 'antd/es/table/interface';
import type React from 'react';
import { memo, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useResizeObserver } from 'usehooks-ts';
import { TestTubeColor } from '../../../components/TestTubeColor';
import type { ParameterSelection_RequestableParameterFragment, ParameterToAddInput } from '../../../graphql/generated/graphql.ts';
import type { SelectedParameter } from '../../../hooks/store/useAppStore';
import { tableActionCell, tableHoverPointer } from '../../../styles/globalCss';
import { billingTypeTagLabel, billingTypeTagLabels, isLgBillingType, testTubeColorProperties } from '../../../utils/enumHelpers';
import { getUniqueLabs } from '../../../utils/labs';
import { AcuteFilterSwitch } from './parameters/AcuteFilterSwitch.tsx';
import { FaLgFilterSwitch } from './parameters/FaLgFilterSwitch.tsx';

const { Option } = Select;

const useClassNames = () => {
  const { token } = theme.useToken();

  return useMemo(() => {
    return {
      tableWrapper: css`
        overflow: auto;
        flex-grow: 1;
      `,
      table: css`
        .ant-table-tbody-virtual .ant-table-cell {
          display: flex;
          align-items: center;
        }
      `,
      labSelect: css`
        width: auto;
        flex-grow: 1;
        min-width: 130px;
      `,
      searchInput: css`
        width: auto;
        flex-grow: 1;
        min-width: 130px;
      `,
      actionsCol: cx(
        tableActionCell,
        css`
          justify-content: right;
        `
      ),
      // Kill the cell's vertical padding and stretch the clickable area to the
      // full row height, so clicks near the top/bottom hit the tags, not the row.
      billingBodyCell: css`
          padding-block: 0 !important;
          align-items: stretch !important;
      `,
      // At rest the cell shows the available billing types as chips (info). On hover
      // they swap for a text CTA ("Wählen"/"Ändern") that opens the billing dialog.
      billingCell: (disabled: boolean) => css`
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        cursor: ${disabled ? 'not-allowed' : 'pointer'};

        .billing-tags {
          display: flex;
          gap: ${token.paddingXXS}px;
          align-items: center;
        }

        .ant-tag {
          ${disabled ? `color: ${token.colorTextDisabled};` : ''}
        }

        .billing-cta {
          display: none;
          align-items: center;
          color: ${token.colorPrimary};
          font-size: ${token.fontSizeSM}px;
          font-weight: 600;
          white-space: nowrap;
        }

        ${
          disabled
            ? ''
            : `&:hover .billing-tags { display: none; }
               &:hover .billing-cta { display: inline-flex; }`
        }
      `,
      billingTag: css`
        margin-inline-end: 0;
        display: inline-flex;
        align-items: center;
        height: 20px;
        padding-inline: ${token.paddingXS}px;
        font-size: ${token.fontSizeSM}px;
        font-weight: 500;
        line-height: 1;
        color: ${token.colorTextSecondary};
        background: ${token.colorFillTertiary};
        border: none;
        border-radius: ${token.borderRadiusSM}px;
      `,
      // Chip of the billing type actually chosen for a selected parameter.
      // box-shadow ring instead of a border: no layout box change, so no jump.
      billingTagSelected: css`
        color: ${token.colorPrimary};
        box-shadow: inset 0 0 0 1px ${token.colorPrimary};
      `,
      acuteIcon: (disabled: boolean) => css`
        font-size: 16px;
        color: ${disabled ? token.colorTextDisabled : token.colorText};
        cursor: ${disabled ? 'not-allowed' : 'inherit'};
      `,
      ellipsis: css`
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      `,
    };
  }, [token]);
};

const TABLE_HEADER_HEIGHT = 40;

// Fixed column widths (single source: used both in the column defs and in the
// remaining-width calculation for the growing Langbezeichnung column).
const CHECKBOX_COL_WIDTH = 24;
const SHORT_NAME_COL_WIDTH = 110;
const ACUTE_COL_WIDTH = 70;
const LAB_COL_WIDTH = 80;
const ACTIONS_COL_WIDTH = 50;
const LONG_NAME_MIN_WIDTH = 160;

interface ParametersProps {
  params: readonly ParameterSelection_RequestableParameterFragment[];
  selectedParams: SelectedParameter[];
  flipParamNames: boolean;
  onAdd: (paramInput: ParameterToAddInput, chooseBilling: boolean) => void;
  onRemove: (paramId: string) => void;
  onEdit: (param: SelectedParameter) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
}

const Parameters: React.FC<ParametersProps> = ({ params, selectedParams, flipParamNames, onAdd, onRemove, onEdit, onInfo }) => {
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [selectedLabId, setSelectedLabId] = useState<string | null>(null);
  const [acuteOnly, setAcuteOnly] = useState(false);
  const [lgOnly, setLgOnly] = useState(false);
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const classNames = useClassNames();

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const { height = 0, width: wrapperWidth = 0 } = useResizeObserver({
    ref: tableWrapperRef as React.RefObject<HTMLElement>, // https://github.com/juliencrn/usehooks-ts/issues/663
    box: 'border-box',
  });

  const searchTokens = useMemo(() => tokenize(debouncedSearch), [debouncedSearch]);

  const filteredParams: ParameterSelection_RequestableParameterFragment[] = useMemo(
    () =>
      params.filter(param => {
        if (!matchesTokens(param, searchTokens, [p => p.shortName, p => p.longName, p => p.synonyms])) {
          return false;
        }
        if (selectedLabId) {
          const match = param.lab.id === selectedLabId;
          if (!match) {
            return false;
          }
        }
        if (acuteOnly) {
          if (!param.acute) {
            return false;
          }
        }
        if (lgOnly) {
          if (!param.billingInfos.some(bi => isLgBillingType(bi.billingType))) {
            return false;
          }
        }
        return true;
      }) ?? [],
    [searchTokens, selectedLabId, acuteOnly, lgOnly, params]
  );

  const handleClick = (parameter: ParameterSelection_RequestableParameterFragment, chooseBilling: boolean) => {
    if (isAddDisabled(parameter.id)) {
      message.warning('Der Parameter ist nicht mit den bereits ausgewählten Parametern kompatibel');
      return;
    }

    if (selectedParams.map(sp => sp.id).includes(parameter.id)) {
      onRemove(parameter.id);
      return;
    }
    onAdd({ parameterId: parameter.id, preferredBillingType: null, preferredDiagnoseIds: [] }, chooseBilling);
  };

  const isAddDisabled = (id: string) => selectedParams.flatMap(sp => sp.withoutParameterIds).includes(id);

  const availableLabs = useMemo(() => getUniqueLabs(params), [params]);

  const hasAcuteParams = useMemo(() => params.some(it => it.acute), [params]);
  const hasLgParams = useMemo(() => params.some(it => it.billingInfos.some(bi => isLgBillingType(bi.billingType))), [params]);

  // Virtual antd tables need a fixed column width (no CSS content-sizing), so size the
  // Verrechnung column to the widest chip combination that actually occurs. Text is
  // measured with the real chip font; paddings come from the same tokens the chips use.
  const billingColWidth = useMemo(() => {
    const MIN_WIDTH = 104; // fits the "Verrechnung" header + "Wählen"/"Ändern" hover CTA
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) {
      return 165;
    }
    ctx.font = `500 ${token.fontSizeSM}px ${token.fontFamily}`;
    const chipPadding = token.paddingXS * 2;
    const cellPadding = token.paddingXS * 2;
    // There are only a handful of distinct labels; measure each one once.
    const chipWidthCache = new Map<string, number>();
    const chipWidth = (label: string): number => {
      const cached = chipWidthCache.get(label);
      if (cached !== undefined) {
        return cached;
      }
      const width = ctx.measureText(label).width + chipPadding;
      chipWidthCache.set(label, width);
      return width;
    };
    let widest = MIN_WIDTH;
    for (const param of params) {
      const labels = billingTypeTagLabels(param.billingInfos.map(bi => bi.billingType));
      if (!labels.length) {
        continue;
      }
      const width = labels.reduce((sum, label) => sum + chipWidth(label), 0) + (labels.length - 1) * token.paddingXXS + cellPadding;
      widest = Math.max(widest, width);
    }
    return Math.ceil(widest);
  }, [params, token.fontSizeSM, token.fontFamily, token.paddingXS, token.paddingXXS]);

  // Proben shows fixed-size test-tube dots, so size the column to the largest tube
  // count that occurs (dot count + gaps), rather than reserving a fixed width.
  const probenColWidth = useMemo(() => {
    const TUBE_DOT = 10; // TestTubeColor badge dot size
    const MIN_WIDTH = 70; // fits the "Proben" header
    let maxTubes = 0;
    for (const param of params) {
      const tubes = param.specimens.reduce((sum, specimen) => sum + specimen.testTubes.length, 0);
      maxTubes = Math.max(maxTubes, tubes);
    }
    if (maxTubes === 0) {
      return MIN_WIDTH;
    }
    const width = maxTubes * TUBE_DOT + (maxTubes - 1) * token.paddingXXS + token.paddingXS * 2;
    return Math.max(MIN_WIDTH, Math.ceil(width));
  }, [params, token.paddingXXS, token.paddingXS]);

  // Langbezeichnung is the flexible column: it grows to fill whatever horizontal space
  // the fixed/content-sized columns leave. Virtual tables have no CSS flex-grow, so it
  // is computed from the measured container width.
  const longNameColWidth = useMemo(() => {
    const scrollbarAllowance = token.paddingSM; // keep the vertical scrollbar from forcing horizontal scroll
    const fixedColumns =
      CHECKBOX_COL_WIDTH +
      SHORT_NAME_COL_WIDTH +
      (hasAcuteParams ? ACUTE_COL_WIDTH : 0) +
      (availableLabs.length > 1 ? LAB_COL_WIDTH : 0) +
      billingColWidth +
      probenColWidth +
      ACTIONS_COL_WIDTH +
      scrollbarAllowance;
    return Math.max(LONG_NAME_MIN_WIDTH, Math.floor(wrapperWidth - fixedColumns));
  }, [wrapperWidth, hasAcuteParams, availableLabs.length, billingColWidth, probenColWidth, token.paddingSM]);

  const shortNameCol: ColumnType<ParameterSelection_RequestableParameterFragment> = {
    title: 'Kurzname',
    dataIndex: 'shortName',
    key: 'shortName',
    ellipsis: true,
    defaultSortOrder: flipParamNames ? undefined : 'ascend',
    width: SHORT_NAME_COL_WIDTH,
    sorter: (a, b) => a.shortName.localeCompare(b.shortName),
    render: (value, record) => {
      const disabled = isAddDisabled(record.id);
      return (
        <div className={classNames.ellipsis}>
          <span
            title={value}
            style={{
              fontFamily: token.fontFamilyCode,
              color: disabled ? token.colorTextDisabled : flipParamNames ? token.colorTextSecondary : token.colorText,
              fontWeight: flipParamNames ? 'normal' : 500,
            }}
          >
            <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
          </span>
        </div>
      );
    },
  };

  const longNameCol: ColumnType<ParameterSelection_RequestableParameterFragment> = {
    title: 'Langbezeichnung',
    dataIndex: 'longName',
    key: 'longName',
    ellipsis: true,
    defaultSortOrder: flipParamNames ? 'ascend' : undefined,
    width: longNameColWidth,
    sorter: (a, b) => a.longName.localeCompare(b.longName),
    render: (value, record) => {
      const disabled = isAddDisabled(record.id);
      return (
        <div className={classNames.ellipsis}>
          <span
            title={value}
            style={{
              color: disabled ? token.colorTextDisabled : flipParamNames ? token.colorText : token.colorTextSecondary,
              fontWeight: flipParamNames ? 500 : 'normal',
            }}
          >
            <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
          </span>
        </div>
      );
    },
  };

  const columns: ColumnsType<ParameterSelection_RequestableParameterFragment> = [
    ...(flipParamNames ? [longNameCol, shortNameCol] : [shortNameCol, longNameCol]),
    ...(hasAcuteParams
      ? [
          {
            title: 'Akut',
            dataIndex: 'acute',
            key: 'acute',
            width: ACUTE_COL_WIDTH,
            align: 'center' as AlignType,
            sorter: (a: ParameterSelection_RequestableParameterFragment, b: ParameterSelection_RequestableParameterFragment) =>
              Number(a.acute) - Number(b.acute),
            render: (value: boolean, record: ParameterSelection_RequestableParameterFragment) => {
              const disabled = isAddDisabled(record.id);
              return value ? <ClockCircleOutlined className={classNames.acuteIcon(disabled)} /> : '';
            },
          },
        ]
      : []),
    ...(availableLabs.length > 1
      ? [
          {
            title: 'Labor',
            dataIndex: ['lab', 'shortName'],
            key: 'lab.shortName',
            ellipsis: true,
            width: LAB_COL_WIDTH,
            sorter: (a: ParameterSelection_RequestableParameterFragment, b: ParameterSelection_RequestableParameterFragment) =>
              a.lab.shortName.localeCompare(b.lab.shortName),
            render: (value: string, record: ParameterSelection_RequestableParameterFragment) => {
              const disabled = isAddDisabled(record.id);
              return (
                <div className={classNames.ellipsis}>
                  <span
                    title={value}
                    style={{
                      color: disabled ? token.colorTextDisabled : token.colorTextSecondary,
                    }}
                  >
                    {value}
                  </span>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: 'Verrechnung',
      dataIndex: 'billingTypes',
      key: 'billingTypes',
      width: billingColWidth,
      onCell: () => ({ className: classNames.billingBodyCell }),
      render: (_, record) => {
        const disabled = isAddDisabled(record.id);
        const tagLabels = billingTypeTagLabels(record.billingInfos.map(bi => bi.billingType));
        const selectedParam = selectedParams.find(sp => sp.id === record.id);
        const selectedLabel = selectedParam ? billingTypeTagLabel(selectedParam.billingInfo.billingType) : null;

        return (
          <div
            className={classNames.billingCell(disabled)}
            onClick={
              disabled
                ? undefined
                : e => {
                    e.stopPropagation();
                    // Selected: open the dialog on the existing choice ("Ändern").
                    // Not selected: add and force the billing dialog ("Wählen").
                    if (selectedParam) {
                      onEdit(selectedParam);
                    } else {
                      handleClick(record, true);
                    }
                  }
            }
          >
            <div className="billing-tags">
              {tagLabels.map(label => (
                <Tag key={label} className={cx(classNames.billingTag, label === selectedLabel && classNames.billingTagSelected)}>
                  {label}
                </Tag>
              ))}
            </div>
            <span className="billing-cta">{selectedParam ? 'Ändern' : 'Wählen'}</span>
          </div>
        );
      },
    },
    {
      title: 'Proben',
      dataIndex: 'testTubes',
      width: probenColWidth,
      key: 'testTubes',
      render: (_, record) => (
        <Flex gap={token.paddingXXS}>
          {record.specimens
            .flatMap(it => it.testTubes)
            .map((tt, i) => (
              <div key={`${tt.id}_${i}`} title={tt.name}>
                <TestTubeColor withText={false} properties={testTubeColorProperties(tt.color)} />
              </div>
            ))}
        </Flex>
      ),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: ACTIONS_COL_WIDTH,
      className: classNames.actionsCol,
      render: (_, record) => (
        <Button
          icon={<InfoCircleOutlined />}
          type="text"
          onClick={e => {
            e.stopPropagation();
            onInfo(record);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Flex align="center" wrap gap={token.paddingSM}>
        <Input
          className={classNames.searchInput}
          allowClear
          placeholder="Suche nach Parameter"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Kurzname, Langbezeichnung und Synonyme">
              <InfoCircleOutlined />
            </Tooltip>
          }
        />
        {availableLabs.length > 1 && (
          <Select
            allowClear
            onChange={labId => setSelectedLabId(labId)}
            value={selectedLabId}
            placeholder="Labors filtern"
            className={classNames.labSelect}
          >
            {availableLabs.map(lab => (
              <Option key={lab.id} value={lab.id}>
                {lab.name}
              </Option>
            ))}
          </Select>
        )}
        <Flex gap={token.paddingSM}>
          {hasAcuteParams && <AcuteFilterSwitch onChange={setAcuteOnly} />}
          {hasLgParams && <FaLgFilterSwitch onChange={setLgOnly} />}
        </Flex>
      </Flex>

      <p />
      <div ref={tableWrapperRef} className={classNames.tableWrapper}>
        <Table<ParameterSelection_RequestableParameterFragment>
          virtual
          scroll={{ y: height - TABLE_HEADER_HEIGHT }}
          showSorterTooltip={false}
          className={classNames.table}
          rowClassName={tableHoverPointer}
          size="small"
          rowKey={row => row.id}
          onRow={record => {
            return {
              onClick: e => {
                e.preventDefault();
                handleClick(record, false);
              },
            };
          }}
          rowSelection={{
            columnWidth: CHECKBOX_COL_WIDTH,
            type: 'checkbox',
            hideSelectAll: true,
            selectedRowKeys: selectedParams.map(p => p.id),
            onSelect: (record: ParameterSelection_RequestableParameterFragment, selected: boolean) => {
              if (isAddDisabled(record.id)) {
                message.warning('Der Parameter ist nicht mit den bereits ausgewählten Parametern kompatibel');
                return;
              }
              selected
                ? onAdd({ parameterId: record.id, preferredBillingType: null, preferredDiagnoseIds: [] }, false)
                : onRemove(record.id);
            },
          }}
          pagination={false}
          dataSource={filteredParams}
          columns={columns}
        />
      </div>
    </>
  );
};

export const MemoParameters = memo(Parameters);
